import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import { AuthedImage } from '@/components/AuthedImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';
import type { ItemSummary } from '@/types/api';

interface Props {
  /** `undefined` while the dashboard stats query is still loading. */
  count: number | undefined;
  /**
   * Soonest-expiring-first preview behind `count` (see ItemService.stats()
   * on the backend), already fetched as part of the dashboard stats call --
   * this component makes no network request of its own.
   */
  expiringItems: ItemSummary[];
}

/**
 * Formats an item's warranty expiry date for the preview list, falling back
 * to the raw ISO string if the active locale can't be resolved by Intl.
 */
function formatWarrantyDate(iso: string | null, locale: string) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * The dashboard's "warranty expiring soon" stat card. Unlike the other stat
 * cards it isn't a plain link to a filtered list page -- there's no
 * warranty-filtered items view -- so it expands in place to show the actual
 * items behind the count, reusing data the dashboard already fetched (issue
 * #78). A zero count can't be expanded into an empty list; it just shows a
 * "none expiring" caption instead.
 */
export function WarrantyExpiringCard({ count, expiringItems }: Props) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasItems = (count ?? 0) > 0;

  return (
    <Card>
      <button
        type="button"
        className="w-full rounded-lg text-left disabled:cursor-default"
        aria-expanded={hasItems ? expanded : undefined}
        aria-controls="warranty-expiring-list"
        onClick={() => setExpanded((prev) => !prev)}
        disabled={!hasItems}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm">{t('dashboard.warrantyExpiring')}</CardTitle>
          <div className="flex items-center gap-1">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {hasItems &&
              (expanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{count ?? '–'}</div>
          {count === 0 && <div className="mt-1 text-xs text-muted-foreground">{t('dashboard.warrantyNoneExpiring')}</div>}
        </CardContent>
      </button>
      {hasItems && expanded && expiringItems.length === 0 && (
        // count > 0 but the preview list is empty: not a real zero, but a
        // brief window where this device hit a backend instance mid-rollout
        // that hasn't shipped warrantyExpiringItems yet (same field, same
        // request as `count`, so this is a deploy-skew artifact, not
        // something a retry within this session would fix). Same reasoning
        // as the Android panel's rollout-skew fallback for issue #78.
        <CardContent id="warranty-expiring-list" className="pt-0">
          <p className="text-xs text-muted-foreground">{t('dashboard.warrantyPreviewUnavailable')}</p>
        </CardContent>
      )}
      {hasItems && expanded && expiringItems.length > 0 && (
        <CardContent id="warranty-expiring-list" className="pt-0">
          <ul className="divide-y">
            {expiringItems.map((it) => {
              const date = formatWarrantyDate(it.warrantyUntil, i18n.language);
              return (
                <li key={it.id}>
                  <Link to={`/items/${it.id}`} intent="standalone" className="flex items-center gap-2 rounded-md px-1 py-2 text-sm">
                    {it.thumbnailPath ? (
                      <AuthedImage src={it.thumbnailPath} alt={it.name} className="h-8 w-8 shrink-0 rounded object-cover" />
                    ) : (
                      <div className="h-8 w-8 shrink-0 rounded bg-muted" aria-hidden="true" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{it.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{date ?? '—'}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </CardContent>
      )}
    </Card>
  );
}
